import type { Coding } from '@medplum/fhirtypes'
import { orderBy, uniq, uniqBy } from 'lodash-es'
import {
  type FuzzySearch,
  fuzzySearch,
  fuzzySearchMatch,
  SEARCH_MATCH,
  type SearchMatch,
  type SearchName,
  type SpellingDictionary,
  searchName,
  spellingDictionary,
} from '#src/fuzzyTerms.ts'
import { searchTerms } from '#src/searchTerms.ts'
import {
  type CapturableQuestion,
  capturableQuestions,
} from '#src/terminology/capturable.ts'
import {
  COMMON_OBSERVATION_CODES,
  isCommonObservationCode,
} from '#src/terminology/commonObservations.ts'
import {
  INSTRUMENT_TEMPLATES,
  instrumentShortName,
} from '#src/terminology/instruments/registry.ts'
import type { InstrumentTemplate } from '#src/terminology/instruments/types.ts'
import { TERMINOLOGY_LIBRARY } from '#src/terminology/library.ts'
import {
  OBSERVATION_SHORTHAND,
  type ObservationShorthand,
  shorthandFor,
} from '#src/terminology/shorthand.ts'

// Where a row of an observation search ranks, first to last.
export const OBSERVATION_SEARCH_TIER = {
  // A shorthand alias typed in full: "wt", "a1c", "phq9".
  shorthand: 0,
  // A code clinicians record most (COMMON_OBSERVATION_CODES).
  common: 1,
  // One of our own question codes.
  library: 2,
  // A validated tool recorded as observations.
  instrument: 3,
  // The rest of LOINC.
  standard: 4,
} as const
export type ObservationSearchTier =
  (typeof OBSERVATION_SEARCH_TIER)[keyof typeof OBSERVATION_SEARCH_TIER]

export interface ObservationSearchHit {
  system: string
  code: string
  display: string
  definition?: string
  // The instrument whose panel this is.
  instrument?: string
  tier: ObservationSearchTier
  match: SearchMatch
}

export interface ObservationSearchOptions {
  includeLibrary: boolean
  includeInstruments: boolean
}

const LIBRARY_MATCHES = 10

// Wording that marks a LOINC name as a qualified or derived form of the plain
// observable ("Body weight Set", "Fetal ...", "... --lean"). The weight is how
// far it sinks among rows that otherwise match alike; a search that uses the
// wording itself is not penalised for it.
const PENALTY_PATTERNS: readonly (readonly [RegExp, number])[] = [
  [/\bdeprecated\b/i, 5000],
  [/\bdiscouraged\b/i, 3000],
  [/\bSet\b$/, 400],
  [/\bfetal\b/i, 300],
  [/--/, 250],
  [/\bpanel\b.*\bprotocol\b/i, 200],
  [
    /\b(lean|ideal|dry|estimated|stated|first|last|baseline|predicted|calculated|corrected)\b/i,
    180,
  ],
  [/\b(pre|post)[- ](dialysis|operative|partum|exercise|dose)\b/i, 200],
  [/\bby\b.*\bmethod\b/i, 60],
  [/\bPhenX\b/, 250],
  [/\b(ambulance|EMS|neonatal|veterinary|animal)\b/i, 200],
]
const SCORE_ROW = /\btotal score\b|\bscore\s*\[/i
const SCORE_QUERY = /\b(score|total)\b/i
// A display that starts as the search does, with or without its qualifiers.
const LEADS_BONUS = 300
const IS_NAME_BONUS = 600

// Words of LOINC names a search is spell-corrected to beyond those of our own
// codes, so a misspelled search still reaches the terminology server.
const COMMON_LOINC_WORDS = [
  'albumin',
  'alcohol',
  'allergy',
  'antibody',
  'antigen',
  'assessment',
  'audit',
  'concentration',
  'culture',
  'fasting',
  'head',
  'history',
  'housing',
  'immunization',
  'insecurity',
  'measured',
  'medication',
  'mental',
  'nitrogen',
  'pregnancy',
  'prapare',
  'protein',
  'reactive',
  'reported',
  'screening',
  'smoking',
  'status',
  'stimulating',
  'titer',
  'tobacco',
  'ultrasound',
  'urea',
  'vital',
  'alkaline',
  'anxiety',
  'arterial',
  'bicarbonate',
  'bilirubin',
  'calcium',
  'capillary',
  'chloride',
  'circumference',
  'cognitive',
  'depression',
  'ferritin',
  'glomerular',
  'hematocrit',
  'hemoglobin',
  'hormone',
  'leukocytes',
  'magnesium',
  'microalbumin',
  'oximetry',
  'oxygen',
  'phosphatase',
  'phosphate',
  'plasma',
  'platelets',
  'potassium',
  'pressure',
  'questionnaire',
  'respiratory',
  'saturation',
  'serum',
  'sodium',
  'temperature',
  'thyroid',
  'thyrotropin',
  'triglyceride',
  'urine',
  'vitamin',
]

interface IndexedQuestion extends CapturableQuestion {
  names: SearchName[]
}

interface IndexedShorthand {
  entry: ObservationShorthand
  names: SearchName[]
}

interface IndexedInstrument {
  template: InstrumentTemplate
  names: SearchName[]
}

interface ObservationSearchIndex {
  dictionary: SpellingDictionary
  questions: IndexedQuestion[]
  shorthand: IndexedShorthand[]
  instruments: IndexedInstrument[]
}

// The names an instrument is searched by: its id, title and short name.
function instrumentNames(template: InstrumentTemplate): string[] {
  return uniq([template.id, template.title, instrumentShortName(template)])
}

let index: ObservationSearchIndex | undefined

// Built once: our terminology is in source, so it cannot change while the
// page is open.
function searchIndex(): ObservationSearchIndex {
  if (index) {
    return index
  }
  const questions = capturableQuestions(TERMINOLOGY_LIBRARY.questionCodes).map(
    (question) => ({
      ...question,
      names: [
        question.code,
        question.display,
        question.definition ?? '',
      ].flatMap((name) => (name ? [searchName(name)] : [])),
    })
  )
  const shorthand = OBSERVATION_SHORTHAND.map((entry) => ({
    entry,
    names: [
      entry.coding.code ?? '',
      entry.coding.display ?? '',
      ...entry.aliases,
    ].flatMap((name) => (name ? [searchName(name)] : [])),
  }))
  const instruments = INSTRUMENT_TEMPLATES.map((template) => ({
    template,
    names: instrumentNames(template).map(searchName),
  }))
  index = {
    // Corrections prefer the words of the codes recorded most.
    dictionary: spellingDictionary([
      ...OBSERVATION_SHORTHAND.flatMap(({ coding, aliases }) => [
        coding.display ?? '',
        ...aliases,
      ]),
      ...COMMON_OBSERVATION_CODES.map(({ display }) => display ?? ''),
      ...COMMON_LOINC_WORDS,
      ...INSTRUMENT_TEMPLATES.flatMap(instrumentNames),
      ...questions.flatMap(({ display, definition }) => [
        display,
        definition ?? '',
      ]),
    ]),
    questions,
    shorthand,
    instruments,
  }
  return index
}

// A search read against the words our observation codes and common LOINC
// names use, with its misspellings' likely corrections.
export function observationSearch(input: string): FuzzySearch {
  return fuzzySearch(input, searchIndex().dictionary)
}

// The rows our own sources offer for a search: shorthand and common codes,
// our question codes and instruments. A blank search offers no shorthand.
export function localObservationHits(
  input: string,
  search: FuzzySearch,
  { includeLibrary, includeInstruments }: ObservationSearchOptions
): ObservationSearchHit[] {
  const { questions, shorthand, instruments } = searchIndex()
  const typedAlias = new Set(shorthandFor(input))
  const shorthandHits = search.terms.length
    ? shorthand.flatMap(({ entry, names }): ObservationSearchHit[] => {
        if (entry.instrument && !includeInstruments) {
          return []
        }
        const match = typedAlias.has(entry)
          ? SEARCH_MATCH.exact
          : fuzzySearchMatch(search, names)
        if (match === undefined) {
          return []
        }
        const hit: ObservationSearchHit = {
          system: entry.coding.system ?? '',
          code: entry.coding.code ?? '',
          display: entry.coding.display ?? '',
          tier: typedAlias.has(entry)
            ? OBSERVATION_SEARCH_TIER.shorthand
            : commonTier(search, entry.coding.display ?? ''),
          match,
        }
        if (entry.instrument) {
          hit.instrument = entry.instrument
        }
        return [hit]
      })
    : []
  const libraryHits = includeLibrary
    ? rankObservationHits(
        search,
        questions.flatMap(({ names, ...question }): ObservationSearchHit[] => {
          const match = fuzzySearchMatch(search, names)
          return match === undefined
            ? []
            : [{ ...question, tier: OBSERVATION_SEARCH_TIER.library, match }]
        })
      ).slice(0, LIBRARY_MATCHES)
    : []
  const instrumentHits = includeInstruments
    ? instruments.flatMap(({ template, names }): ObservationSearchHit[] => {
        const match = search.terms.length
          ? fuzzySearchMatch(search, names)
          : SEARCH_MATCH.prefix
        return match === undefined
          ? []
          : [
              {
                system: template.panel.system ?? '',
                code: template.panel.code ?? '',
                display: template.title,
                instrument: template.id,
                tier: OBSERVATION_SEARCH_TIER.instrument,
                match,
              },
            ]
      })
    : []
  return [...shorthandHits, ...libraryHits, ...instrumentHits]
}

// A common code's tier. A screening total ranks with the instruments unless
// the search asks for a score, since the instrument records the total too.
function commonTier(
  search: FuzzySearch,
  display: string
): ObservationSearchTier {
  return SCORE_ROW.test(display) && !SCORE_QUERY.test(search.terms.join(' '))
    ? OBSERVATION_SEARCH_TIER.instrument
    : OBSERVATION_SEARCH_TIER.common
}

// A row the terminology server returned. The server matched it already,
// perhaps on a synonym its display lacks, so a row the search cannot match
// locally still counts, as the weakest match.
export function standardObservationHit(
  search: FuzzySearch,
  coding: Required<Pick<Coding, 'system' | 'code' | 'display'>>
): ObservationSearchHit {
  return {
    system: coding.system,
    code: coding.code,
    display: coding.display,
    tier: isCommonObservationCode(coding)
      ? commonTier(search, coding.display)
      : OBSERVATION_SEARCH_TIER.standard,
    match:
      fuzzySearchMatch(search, [
        searchName(coding.code),
        searchName(coding.display),
      ]) ?? SEARCH_MATCH.fuzzy,
  }
}

// A display without its [qualifiers] and (asides), as one string of terms.
function plainName(display: string): string {
  return searchTerms(
    display.replace(/\s*\[.*?\]/g, '').replace(/\s*\(.*?\)/g, '')
  ).join(' ')
}

// How far a row sinks (positive) or rises (negative) among rows of the same
// tier and match, for a search: sunk by the wording of a qualified form,
// raised by a display that starts as the search does.
function rowScore(search: FuzzySearch, display: string): number {
  if (!search.terms.length) {
    return 0
  }
  const typed = search.terms.join(' ')
  const plain = plainName(display)
  let score = 0
  for (const [pattern, penalty] of PENALTY_PATTERNS) {
    if (pattern.test(display) && !pattern.test(typed)) {
      score += penalty
    }
  }
  if (plain === typed) {
    score -= IS_NAME_BONUS
  } else if (plain.startsWith(typed)) {
    score -= LEADS_BONUS
  }
  return score
}

// Rows best first: by tier, then how well they match, then, for a search,
// how plain and leading the display is, then the shorter display (the plain
// observable ahead of its qualified forms). A code offered twice keeps its
// best row.
export function rankObservationHits<T extends ObservationSearchHit>(
  search: FuzzySearch,
  hits: readonly T[]
): T[] {
  const ranked = orderBy(
    hits,
    [
      'tier',
      'match',
      (hit: T) => rowScore(search, hit.display),
      (hit: T) => (search.terms.length ? hit.display.length : 0),
    ],
    ['asc', 'asc', 'asc', 'asc']
  )
  return uniqBy(ranked, ({ system, code }) => `${system}|${code}`)
}
